import {
  showErrorToast,
  showSuccessToast,
} from "components/CustomAlerts/CustomToast";
import { Input } from "components/Input/Input";
import { useFormik } from "formik";
import { TituloI } from "interfaces/Titulo";
import { useEffect, useState } from "react";
import { Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { createTitulosSchema } from "schemas/titulos/createTitulosSchema";
import { postTitulo, putTitulo } from "services/titulos";
import { RootState } from "store/store";
import { AsyncSelect } from "components/AsyncInput/AsyncSelect";
import { getNiveles } from "services/niveles";
import { NivelI } from "interfaces/Nivel";

interface Props {
  actionType: "create" | "edit" | undefined;
  onClose?: any;
  elemento?: TituloI;
  onSuccess?: (titulo: TituloI) => void;
  showModal: boolean;
}

interface initialValues {
  id?: string | number;
  nombre: string;
  descripcion: string;
  nivel_id?: string | number;
}

interface defaultOptionsSelected {
  default_selected_nivel: NivelI | null;
}

export const TitulosABM = ({
  showModal,
  actionType,
  onClose,
  elemento,
  onSuccess,
}: Props) => {
  const isEditMode = actionType == "edit";
  const initialValues: initialValues = {
    nombre: "",
    descripcion: "",
    nivel_id: "",
  };
  const [isLoading, setIsLoading] = useState(false);
  const [defaultOptionsSelected, setDefaultOptionsSelected] =
    useState<defaultOptionsSelected>({
      default_selected_nivel: null,
    });

  const renderInitialValues = async () => {
    let currentValues;

    if (isEditMode && elemento) {
      currentValues = {
        id: elemento?.id ?? "",
        nombre: elemento?.nombre ?? "",
        descripcion: elemento?.descripcion ?? "",
        nivel_id: elemento?.nivel.id ?? "",
      };
      setDefaultOptionsSelected({
        default_selected_nivel: {
          id: elemento.nivel.id,
          nombre: elemento.nivel.nombre,
        },
      });
    } else {
      currentValues = initialValues;
    }
    setValues(currentValues);
  };

  const handleSubmitCreate = async (params: any) => {
    try {
      setIsLoading(true);
      const newTitulo = await postTitulo({ params });
      showSuccessToast("¡El título fue creado con éxito!");
      if (onSuccess) onSuccess(newTitulo);
      onClose();
    } catch (err: any) {
      showErrorToast("El título no pudo ser creado.");
      console.log(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitEdit = async (params: any) => {
    try {
      setIsLoading(true);
      const newTitulo = await putTitulo({ params });
      showSuccessToast("¡El título fue editado con éxito!");
      if (onSuccess) onSuccess(newTitulo);
      onClose();
    } catch (err: any) {
      showErrorToast("El título no pudo ser editado.");
      console.log(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    isSubmitting,
    handleSubmit,
    setValues,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema: createTitulosSchema,
    onSubmit: actionType == "create" ? handleSubmitCreate : handleSubmitEdit,
  });

  const { loadingAppData, tiposNormativa } = useSelector(
    (st: RootState) => st.appData,
  );

  useEffect(() => {
    resetForm();
    if (actionType) {
      renderInitialValues();
    }
  }, [actionType]);

  return (
    <>
      <Modal show={showModal} onHide={onClose} size="lg" centered>
        <Modal.Header>
          <Modal.Title>
            {isEditMode ? "Editar Título" : "Nuevo Título"}
          </Modal.Title>
        </Modal.Header>

        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <Col>
              <Row>
                <Col sm={6}>
                  <Input
                    id={"nombre"}
                    title="Nombre del título"
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
                <Col sm={6}>
                  <Input
                    id={"descripcion"}
                    title="Descripción"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} className="mt-1 mb-4">
                  <AsyncSelect
                    onChange={handleChange}
                    id="nivel_id"
                    values={values}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    placeholder={"Buscar nombre del nivel"}
                    size="lg"
                    title="Nivel"
                    getter={getNiveles}
                    paramKey={"nombre"}
                    variant="gray-container"
                    arrElementsPath="elementos"
                    optionsFormatConfig={{
                      value: "id",
                      label: (el: NivelI) => `${el.nombre}`,
                    }}
                    defaultSelectedOptions={
                      defaultOptionsSelected["default_selected_nivel"]
                    }
                    required
                    noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                    emptySelectMessage={`Ingresar un nombre`}
                  />
                </Col>
              </Row>
            </Col>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="mx-3"
              variant="secondary"
              size="lg"
              onClick={onClose}
            >
              Cancelar
            </Button>
            {!isLoading && (
              <Button
                variant="primary"
                size="lg"
                className="user-submit-button"
                /* disabled={isSubmitting} */ type="submit"
              >
                {isEditMode ? "Guardar Cambios" : "Confirmar"}
              </Button>
            )}

            {isLoading && (
              <div className="d-flex justify-content-center w-full">
                <Spinner />
              </div>
            )}
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};
